import Link from 'next/link';

import { forwardRef, HTMLAttributes, MouseEvent, useMemo, useState } from 'react';

import { format } from 'date-fns';

import ImageProxy from '@next-image-proxy';

import { UpdatingKey } from '@query';
import { useDeleteLikeBlogMutation, useSetLikeBlogMutation } from '@query';

import { Blog } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import displayErrorToast from '@shared/helpers/displayErrorToast';

// Share
import { Eyes, Like, LikeFull } from '@ui/uikit/components/icons/mono';
import { cn, formatAmountCompact } from '@ui/uikit/utils';

type Props = {
  article: Blog;
  // isTextShare?: boolean;
  updatingKey: UpdatingKey;
  className?: string;
};

export const LinkeAndShare = ({ article, updatingKey, className }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { isAuth, user } = useAuth();
  const { mutateAsync: setLike } = useSetLikeBlogMutation();
  const { mutateAsync: deleteLike } = useDeleteLikeBlogMutation();
  const authModal = useAuthModal();

  const handlerLike = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!isAuth) {
      return authModal.open();
    }

    try {
      setIsLoading(true);
      const params = { userId: user.attributes.uuid, blogId: article.id, updatingKey };

      if (article.isHasUserLike) {
        const data = await deleteLike(params);
        return data;
      }

      const data = await setLike(params);
      return data;
    } catch (error) {
      displayErrorToast({ error });
    } finally {
      setIsLoading(false);
    }
  };

  // const handlerShare = () => {
  //   if (!isAuth) {
  //     return authModal.open()
  //   }
  // }

  const isHaveLike = useMemo(() => {
    if (isLoading) {
      return !article.isHasUserLike;
    }

    return article.isHasUserLike;
  }, [article, isLoading]);

  const likesCount = useMemo(() => {
    if (isLoading && article.isHasUserLike) {
      return article.countLikes - 1;
    }

    if (isLoading && !article.isHasUserLike) {
      return article.countLikes + 1;
    }

    return article.countLikes;
  }, [article, isLoading]);

  return (
    <div className={cn('flex gap-4', className)}>
      <button
        className={cn(
          'button button-text-primary py-1.5 px-3 bg-button-secondary-default-bg hover:bg-button-secondary-hover-bg transition-all rounded-lg',
          {
            'bg-base-bg-secondary-pink hover:bg-base-bg-secondary-pink': isHaveLike,
            disabled: isLoading,
          },
        )}
        onClick={handlerLike}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {isHaveLike || isHovering ? (
          <LikeFull className={cn('mr-1.5 size-4 text-base-text-pink')} />
        ) : (
          <Like className={cn('mr-1.5 size-4 text-base-text-primary')} />
        )}
        <span className="text-button-secondary-default-text text-caption-sm-strong-a">
          {likesCount}
        </span>
      </button>
      <div className="flex justify-start items-center gap-[6px] font-semibold text-base-text-tertiary text-caption-sm-strong-a">
        <Eyes className="size-4 text-base-text-tertiary" />{' '}
        {formatAmountCompact(article.countViews || 0)}
      </div>
      {/* <button
        className="button bg-button-secondary-default-bg button-text-primary p-2 rounded-lg gap-2"
        onClick={handlerShare}
      >
        <Share className="size-4 text-base-text-primary" />
        {isTextShare && (
          <span className="text-button-secondary-default-text text-caption-sm-strong-a">Share</span>
        )}
      </button> */}
    </div>
  );
};

export const BlogCard = ({ article, updatingKey }: Props) => {
  return (
    <Link
      href={`/blog/${article.slug}`}
      className="flex flex-col rounded-3xl border border-base-border-medium"
    >
      <ImageProxy
        width={332}
        height={186}
        quality={100}
        src={article?.image?.url}
        className="w-auto h-full rounded-t-3xl select-none aspect-[16/9]"
        alt={article.slug}
      />
      <div className="p-6">
        <span className="mb-2 text-caption-xs-d text-base-text-tertiary">
          {format(new Date(article.createdAt), 'dd MMM yyyy')}
        </span>
        <h3 className="mb-4 text-caption-md-strong-a text-base-text-primary  h-[48px] line-clamp-2">
          {article.title}
        </h3>
        {article?.category && (
          <div className="inline-block w-auto mb-5 px-2 py-1 rounded-full bg-base-bg-secondary text-base-text-tertiary text-caption-xs-d">
            {article.category.name}
          </div>
        )}
        <LinkeAndShare article={article} updatingKey={updatingKey} />
      </div>
    </Link>
  );
};

export const SkeletonList = forwardRef<
  HTMLDivElement,
  {
    limit?: number;
  } & HTMLAttributes<HTMLDivElement>
>(({ limit = 8, ...props }, ref) => {
  const bloksIds = [...Array(limit).keys()];
  return (
    <div
      ref={ref}
      className="
      grid gap-2 md:gap-4
      pt-2 md:pt-4
      grid-cols-1
      sm:grid-cols-2
      md:grid-cols-2
      lg:grid-cols-3
      xl:grid-cols-4
    "
      {...props}
    >
      {bloksIds.map((id) => (
        <div
          key={id}
          className="rounded-3xl border border-base-border-medium skeleton-block h-[460px]"
        />
      ))}
    </div>
  );
});
